/**
 * 공개 페이지(랜딩·소개) 임베드용 데모 투어 — 디바이스 프레임 안에 실제 사업장 관리 투어를 가둔다.
 *  - 비로그인 방문자도 동작(공개 스냅샷 + 게스트 안전 목 주입).
 *  - 성능: 뷰포트 근처에 들어올 때만(IntersectionObserver) 플레이어를 마운트해 초기 번들/렌더 부담 최소화.
 *  - 명함 QR 유입이 많은 /about, 랜딩 '/' 양쪽에서 재사용.
 */
import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FiArrowRight, FiPlay } from 'react-icons/fi'
import Spinner from '../../components/ui/Spinner'

const OnboardingTourPlayer = lazy(() => import('./OnboardingTourPlayer'))

export default function OnboardingTourEmbed({
  scrollId = 'onb-embed-scroll',
  eyebrow = '제품 둘러보기 · 가입 없이',
  title = '실제 화면으로, 1분 만에 둘러보세요',
  subtitle = '예시 사업장 「Heinrich 정밀」의 실제 분석 결과로 위험성평가·JSA·작업허가서·TBM·법정의무까지 직접 펼쳐봅니다. 로그인 없이 지금 바로.',
}: {
  scrollId?: string
  eyebrow?: string
  title?: string
  subtitle?: string
}) {
  const navigate = useNavigate()
  const ref = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(false)   // 사용자가 '체험 시작' 클릭

  // 뷰포트 근처 진입 시 플레이어 청크를 백그라운드 프리로드(클릭 즉시 시작되도록)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        import('./OnboardingTourPlayer')
        io.disconnect()
      }
    }, { rootMargin: '400px' })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <section className="bg-zinc-950 py-16 sm:py-20" aria-labelledby="onb-embed-title">
      <div className="mx-auto max-w-7xl px-4">
        <div className="text-center mb-8">
          <span className="inline-block rounded-full border border-brand-primary/40 bg-brand-primary/10 px-3 py-1 text-xs font-semibold text-brand-primary">
            {eyebrow}
          </span>
          <h2 id="onb-embed-title" className="mt-3 text-2xl sm:text-3xl font-bold text-white">{title}</h2>
          <p className="mt-2 text-sm sm:text-base text-zinc-400 max-w-2xl mx-auto">{subtitle}</p>
        </div>

        {/* 브라우저 프레임 */}
        <div ref={ref} className="mx-auto max-w-7xl rounded-2xl border border-white/10 bg-zinc-900 shadow-2xl shadow-black/50 overflow-hidden">
          {/* 크롬 바 */}
          <div className="flex items-center gap-2 border-b border-white/10 bg-zinc-800/80 px-4 py-2.5">
            <span className="h-3 w-3 rounded-full bg-red-400/80" />
            <span className="h-3 w-3 rounded-full bg-amber-400/80" />
            <span className="h-3 w-3 rounded-full bg-emerald-400/80" />
            <div className="ml-3 flex-1 truncate rounded-md bg-zinc-700/60 px-3 py-1 text-[11px] text-zinc-300">
              heinrich.co.kr / 대시보드 · 사업장 관리 — Heinrich 정밀 (예시)
            </div>
          </div>

          {/* 투어 영역 — 자체 스크롤. active 전엔 실제 화면 미리보기(블러)+시작 버튼(번들 절약) */}
          <div className="onb-theme-light relative h-[86dvh] max-h-[1040px] min-h-[640px] sm:h-[880px] bg-surface-deep">
            {active ? (
              <Suspense fallback={<div className="flex h-full items-center justify-center"><Spinner size="lg" /></div>}>
                <OnboardingTourPlayer
                  embedded
                  publicBundle
                  scrollRootId={scrollId}
                  finishLabel="무료로 시작하기"
                  onFinish={() => navigate('/signup')}
                />
              </Suspense>
            ) : (
              <button
                onClick={() => { setActive(true) }}
                className="group absolute inset-0 flex flex-col items-center justify-center gap-4 overflow-hidden text-center"
                aria-label="데모 투어 시작">
                {/* 실제 온보딩 화면 미리보기(블러) — 무엇을 보게 될지 즉시 인지 */}
                <img
                  src="/assets/onboarding-preview.png"
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full object-cover object-top scale-105 blur-[6px] brightness-[0.96] select-none pointer-events-none"
                  loading="lazy"
                />
                <span className="absolute inset-0 bg-zinc-950/45" aria-hidden="true" />
                <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary text-white shadow-xl shadow-black/40 ring-4 ring-white/25 transition-transform group-hover:scale-110">
                  <FiPlay size={26} className="ml-1" />
                </span>
                <span className="relative text-base font-semibold text-white drop-shadow">데모 투어 시작</span>
                <span className="relative text-xs text-zinc-200 drop-shadow">클릭하면 실제 사업장 관리 화면이 열립니다 · 13단계 가이드</span>
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/signup" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-primary hover:underline">
            무료로 시작하기 <FiArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  )
}
